import {
  OktonSearchSelect,
  OktonPageHeader,
  OktonInput,
  OktonAlert,
  OktonEmptyState,
} from '@okton/platform-ui';
import {
  Box,
  Button,
  Flex,
  IconButton,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Check, Plus, Trash2, Wallet } from 'lucide-react';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import BankAppPage from '../../../layout/BankAppPage';
import {
  documentoDoCliente,
  useClientes,
} from '../../../services/hooks/useCadastros';
import { useBancos } from '../../../services/hooks/useContasBancarias';
import { ChequeLancamento } from '../../../services/hooks/useLancamentosFinanceirosMutations';
import { formataDocumento, soDigitos } from '../../../utils/documento';
import formatValue from '../../../utils/formatValue';
import { limparEntradaValor, valorParaNumero } from '../../../utils/valorInput';

// valorParaNumero devolve NaN quando o texto não tem número: somar NaN
// contaminaria o total inteiro.
function numero(texto: string): number {
  const valor = valorParaNumero(texto);
  return Number.isNaN(valor) ? 0 : valor;
}

const VAZIO: ChequeLancamento = {
  valor: '',
  documento: '',
  banco: '',
  emitente: '',
  cpfcnpj: '',
  vencimento: '',
  observacao: '',
};

interface EstadoCheques {
  // O formulário do lançamento vai e volta inteiro: esta tela não grava nada,
  // então sair dela não pode custar o que já estava digitado lá.
  form?: unknown;
  cheques?: ChequeLancamento[];
  valor?: string;
}

/**
 * Cadastro dos cheques que liquidam um lançamento.
 *
 * Nada aqui toca o banco de dados. Os cheques voltam para a tela do lançamento
 * e só são gravados quando ele for — no mesmo commit. É o que garante que
 * desistir no meio, ou fechar a aba, não deixe cheque solto na base.
 *
 * No IFLEX o cheque não tem tabela própria: é uma linha do financeiro com o
 * banco preenchido, ligada ao título que ela paga. Os campos aqui são os
 * mesmos que a tela de Cheques de lá pede.
 */
const ChequesLancamento = () => {
  const history = useHistory();
  const { state } = useLocation<EstadoCheques | undefined>();
  const bg = useColorModeValue('white', 'gray.800');
  const subtleBg = useColorModeValue('gray.50', 'gray.700');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');

  const { data: bancos = [] } = useBancos();

  const [cheques, setCheques] = useState<ChequeLancamento[]>(
    state?.cheques ?? [],
  );
  const [novo, setNovo] = useState<ChequeLancamento>({ ...VAZIO });
  const [erro, setErro] = useState('');
  // O emitente é o próprio texto do campo: nomecliente_fin é texto livre no
  // IFLEX, e o emitente pode nem estar no cadastro de Clientes. Escolher um
  // Cliente na lista só preenche esse texto e o documento dele.
  const [clienteEscolhido, setClienteEscolhido] = useState('');
  const { data: clientes = [], isFetching: buscandoClientes } = useClientes(
    novo.emitente,
  );

  const escolherEmitente = (id: string) => {
    setClienteEscolhido(id);
    const cliente = clientes.find(c => c.id_cli === id);
    if (!cliente) return;
    const documento = documentoDoCliente(cliente);
    setNovo(c => ({
      ...c,
      emitente: cliente.nome_cli,
      // Só preenche o que o cadastro do Cliente tem: sem documento lá, o
      // campo fica livre para digitar.
      cpfcnpj: documento !== '' ? documento : c.cpfcnpj,
    }));
  };

  // O valor do lançamento é o alvo: a soma dos cheques tem que fechar com ele,
  // senão o título nasceria liquidado por um valor que ninguém entregou.
  const alvo = numero(state?.valor ?? '');
  const somado = cheques.reduce((total, c) => total + numero(c.valor), 0);
  const falta = alvo - somado;
  // Centavo de tolerância, como o compareTo do IFLEX faz com dinheiro.
  const fecha = Math.abs(falta) < 0.005;

  const voltar = (lista: ChequeLancamento[]) => {
    history.push('/lancamentos/novo', {
      ...(state ?? {}),
      cheques: lista,
    });
  };

  const adicionar = () => {
    const valor = numero(novo.valor);
    if (valor <= 0) {
      setErro('Informe o valor do cheque.');
      return;
    }
    if (!novo.documento.trim()) {
      setErro('Informe o número do cheque.');
      return;
    }
    if (!novo.banco) {
      setErro('Informe o Banco do cheque.');
      return;
    }
    if (!novo.emitente.trim()) {
      setErro('Informe o emitente do cheque.');
      return;
    }
    const documento = novo.cpfcnpj.replace(/\D/g, '');
    if (
      documento !== '' &&
      documento.length !== 11 &&
      documento.length !== 14
    ) {
      setErro('O CPF/CNPJ do emitente precisa ter 11 ou 14 dígitos.');
      return;
    }
    if (!novo.vencimento) {
      setErro('Informe o vencimento do cheque.');
      return;
    }
    setErro('');
    setCheques(atual => [...atual, { ...novo }]);
    setNovo({ ...VAZIO });
    setClienteEscolhido('');
  };

  const remover = (indice: number) => {
    setCheques(atual => atual.filter((_, i) => i !== indice));
  };

  const nomeBanco = (codigo: string) =>
    bancos.find(b => b.id_ban === codigo)?.nome_ban ?? codigo;

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title="Cheques do Lançamento"
        subtitle="Os cheques que liquidam este título"
        onBack={() => history.push('/lancamentos/novo')}
      />

      <Box bg={bg} mt="4" borderWidth="1px" rounded="2xl" p="6">
        <Stack spacing="4">
          {!!erro && <OktonAlert status="error" description={erro} />}

          <Box bg={subtleBg} borderRadius="lg" px="4" py="3">
            <Flex align="center" gridGap="6" flexWrap="wrap">
              <Box>
                <Text fontSize="xs" color="gray.500">
                  Valor do lançamento
                </Text>
                <Text fontWeight="semibold" color={color}>
                  {formatValue(alvo)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">
                  Soma dos cheques
                </Text>
                <Text fontWeight="semibold" color={fecha ? 'green.600' : color}>
                  {formatValue(somado)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">
                  {falta > 0 ? 'Falta' : 'Excedente'}
                </Text>
                <Text
                  fontWeight="semibold"
                  color={fecha ? 'green.600' : 'orange.500'}
                >
                  {fecha ? '—' : formatValue(Math.abs(falta))}
                </Text>
              </Box>
            </Flex>
          </Box>

          {/* A coluna da esquerda tem largura fixa: a lista à direita não pode
          mudar o tamanho dos campos. */}
          <Flex gridGap="8" align="flex-start" flexWrap="wrap">
            <Stack spacing="4" w="100%" maxW="720px">
              <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                Novo cheque
              </Text>

              <SimpleGrid columns={[1, 2]} spacing="4">
                <Box>
                  <Text
                    as="label"
                    htmlFor="cheque-lancamento-valor"
                    display="block"
                    fontSize="sm"
                    fontWeight="medium"
                    mb="1"
                  >
                    Valor *
                  </Text>
                  <OktonInput
                    name="cheque-lancamento-valor"
                    size="lg"
                    rounded="2xl"
                    fontWeight="light"
                    color={color}
                    inputMode="decimal"
                    placeholder="0,00"
                    value={novo.valor}
                    onChange={e => {
                      const valor = e.target.value;
                      setNovo(c => ({
                        ...c,
                        valor: limparEntradaValor(valor),
                      }));
                    }}
                  />
                </Box>
                <Box>
                  <Text
                    as="label"
                    htmlFor="cheque-lancamento-documento"
                    display="block"
                    fontSize="sm"
                    fontWeight="medium"
                    mb="1"
                  >
                    Número do cheque *
                  </Text>
                  <OktonInput
                    name="cheque-lancamento-documento"
                    size="lg"
                    rounded="2xl"
                    fontWeight="light"
                    color={color}
                    maxLength={40}
                    value={novo.documento}
                    onChange={e => {
                      const valor = e.target.value;
                      setNovo(c => ({ ...c, documento: valor }));
                    }}
                  />
                </Box>
              </SimpleGrid>

              <Box>
                <Text
                  as="label"
                  htmlFor="banco"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Banco *
                </Text>
                <OktonSearchSelect
                  name="banco"
                  placeholder="Selecione o Banco"
                  value={novo.banco}
                  onChange={valor => setNovo(c => ({ ...c, banco: valor }))}
                  options={[
                    ...bancos.map(b => ({
                      value: b.id_ban,
                      label: b.nome_ban,
                    })),
                  ]}
                />
              </Box>

              <Box>
                <Text
                  as="label"
                  htmlFor="emitente"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Emitente *
                </Text>
                {/* Mesma busca do campo Cliente do lançamento: a lista vem do
                servidor conforme se digita. Escolher um Cliente preenche o
                nome e o CPF/CNPJ dele. */}
                {/* Um campo só: o que se digita já é o emitente, e a lista
                abaixo é um atalho para quem está no cadastro de Clientes —
                escolher um traz o nome e o CPF/CNPJ dele. */}
                <OktonSearchSelect
                  filterOptions={false}
                  name="emitente"
                  allowFreeText
                  value={clienteEscolhido}
                  onChange={escolherEmitente}
                  search={novo.emitente}
                  onSearchChange={texto => {
                    setNovo(c => ({ ...c, emitente: texto }));
                    setClienteEscolhido('');
                  }}
                  loading={buscandoClientes}
                  placeholder="Nome do emitente"
                  emptyMessage={
                    novo.emitente.trim().length < 2
                      ? 'Digite ao menos 2 caracteres para buscar no cadastro'
                      : 'Nenhum cliente com esse nome — o emitente pode ser digitado assim mesmo'
                  }
                  options={clientes.map(c => ({
                    value: c.id_cli,
                    label: c.nome_cli,
                  }))}
                />
              </Box>

              <SimpleGrid columns={[1, 2]} spacing="4">
                <Box>
                  <Text
                    as="label"
                    htmlFor="cheque-lancamento-cpfcnpj"
                    display="block"
                    fontSize="sm"
                    fontWeight="medium"
                    mb="1"
                  >
                    CPF/CNPJ
                  </Text>
                  <OktonInput
                    name="cheque-lancamento-cpfcnpj"
                    size="lg"
                    rounded="2xl"
                    fontWeight="light"
                    color={color}
                    maxLength={18}
                    placeholder="CPF ou CNPJ"
                    value={formataDocumento(novo.cpfcnpj)}
                    // O estado guarda só os números, que é como o IFLEX grava;
                    // a máscara existe só para a leitura.
                    onChange={e => {
                      const valor = e.target.value;
                      setNovo(c => ({
                        ...c,
                        cpfcnpj: soDigitos(valor),
                      }));
                    }}
                  />
                </Box>
                <Box>
                  <Text
                    as="label"
                    htmlFor="cheque-lancamento-vencimento"
                    display="block"
                    fontSize="sm"
                    fontWeight="medium"
                    mb="1"
                  >
                    Vencimento *
                  </Text>
                  <OktonInput
                    name="cheque-lancamento-vencimento"
                    type="date"
                    size="lg"
                    rounded="2xl"
                    fontWeight="light"
                    color={color}
                    value={novo.vencimento}
                    onChange={e => {
                      const valor = e.target.value;
                      setNovo(c => ({ ...c, vencimento: valor }));
                    }}
                  />
                </Box>
              </SimpleGrid>

              <Box>
                <Text
                  as="label"
                  htmlFor="cheque-lancamento-observacao"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Observação
                </Text>
                <OktonInput
                  name="cheque-lancamento-observacao"
                  size="lg"
                  rounded="2xl"
                  fontWeight="light"
                  color={color}
                  maxLength={200}
                  value={novo.observacao}
                  onChange={e => {
                    const valor = e.target.value;
                    setNovo(c => ({ ...c, observacao: valor }));
                  }}
                />
              </Box>

              <Flex>
                <Button
                  leftIcon={<Plus size={18} />}
                  colorScheme="blue"
                  variant="outline"
                  borderWidth="2px"
                  onClick={adicionar}
                >
                  Adicionar cheque
                </Button>
              </Flex>
            </Stack>

            <Stack spacing="4" flex="1" minW="320px">
              <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                Cheques deste lançamento ({cheques.length})
              </Text>

              {cheques.length === 0 ? (
                <Box borderWidth="1px" borderRadius="lg">
                  <OktonEmptyState
                    icon={Wallet}
                    title="Nenhum cheque adicionado"
                    description="A soma dos cheques precisa fechar com o valor do lançamento."
                  />
                </Box>
              ) : (
                <Stack
                  spacing="0"
                  borderWidth="1px"
                  borderRadius="lg"
                  overflow="hidden"
                >
                  {cheques.map((c, i) => (
                    <Flex
                      // O cheque não tem código antes de ser gravado: a posição
                      // na lista é o que o identifica aqui.
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${c.documento}-${i}`}
                      align="center"
                      gridGap="3"
                      px="4"
                      py="3"
                      borderBottomWidth="1px"
                    >
                      <Box flex="1" minW="0">
                        <Text
                          fontSize="sm"
                          color={color}
                          whiteSpace="normal"
                          wordBreak="normal"
                        >
                          {c.emitente}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          Nº {c.documento} — {nomeBanco(c.banco)}
                          {c.cpfcnpj ? ` — ${formataDocumento(c.cpfcnpj)}` : ''}
                        </Text>
                      </Box>
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color={color}
                        whiteSpace="nowrap"
                        flexShrink={0}
                      >
                        {formatValue(numero(c.valor))}
                      </Text>
                      <IconButton
                        aria-label="Remover"
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        icon={<Trash2 size={16} />}
                        onClick={() => remover(i)}
                      />
                    </Flex>
                  ))}
                </Stack>
              )}
            </Stack>
          </Flex>

          <Flex gridGap="3" pt="2">
            <Button
              variant="ghost"
              onClick={() => voltar(state?.cheques ?? [])}
            >
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              leftIcon={<Check size={16} />}
              isDisabled={!fecha || cheques.length === 0}
              onClick={() => voltar(cheques)}
            >
              Confirmar
            </Button>
            {!fecha && cheques.length > 0 && (
              <Flex align="center">
                <Text fontSize="xs" color="orange.500">
                  A soma dos cheques precisa fechar com o valor do lançamento.
                </Text>
              </Flex>
            )}
          </Flex>
        </Stack>
      </Box>
    </BankAppPage>
  );
};

export default ChequesLancamento;
